import React from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { userPanelApi } from 'api';
import { usePanel } from 'contexts/panelContext';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { Button } from 'components/_common/Button';
import { Pair } from 'types/Pair';
import { Loader } from 'components/_new/Loader';
import { prepareUserAreas } from 'utils/prepareUserAreas';
import { MessageBox } from 'components/_new/MessageBox';
import { Box, BoxSection } from 'components/_new/Box';
import { Link } from 'components/_new/Link';
import PairItem from './Pair';

const Pairing = () => {
	const { t } = useTranslation();
	const {
		panelType,
		currentProgram,
		currentApplicationData: { systemAnswers, applicationRole, limitStats, pairLimit },
		currentProgramMembership: { id: currentProgramMembershipId },
		todosRefetch,
		panelLinkPrefix,
	} = usePanel();
	const pairLimitReached = Boolean(limitStats?.pairLimitReached);

	const sortPairs = (pairs: Pair[]) =>
		[...pairs]
			.map((pair) => {
				const statusValueMap = {
					finished: 6,
					accepted: 5,
					open: 3,
					rejected: -1,
					removed: 0,
				};
				const sortVal =
					statusValueMap[pair.status] +
					Number(!!pair.acceptedByMentee) +
					Number(!!pair.acceptedByMentor) +
					Number(!!pair.entryMeeting) +
					Number(pair.scoring && pair.scoring.sum ? pair.scoring.sum : 0) / 1000;
				return { sortVal, ...pair };
			})
			.sort((a, b) => (a.sortVal > b.sortVal ? -1 : 1));

	const {
		data: userPairsData,
		isFetched,
		isLoading,
		isError,
		refetch,
	} = useQuery(
		[
			'userPairs',
			{
				applicationRole,
				currentProgramMembershipId,
			},
		],
		() => userPanelApi.getPairs(String(applicationRole), Number(currentProgramMembershipId)),
		{
			select: sortPairs,
			enabled: !!(applicationRole && currentProgramMembershipId),
		}
	);
	const userPairs = userPairsData || [];
	const numberOfPairs = Number(limitStats?.currentPairs);

	const userAreas = prepareUserAreas(systemAnswers.areas);

	const handleAction = () => {
		refetch();
		todosRefetch();
	};

	return (
		<>
			<Helmet title={t('misc.pageTitles.pairing')} />
			<SubPageTitle
				title={t('userPanel.pairing.pairPropositions', {
					type: panelType === 'mentee' ? t('misc.mentors') : t('misc.mentees'),
				})}
				additional={
					userPairs.length > 0
						? t('userPanel.pairing.choosenPairs', {
								curNumber: numberOfPairs,
								max: pairLimit,
						  })
						: undefined
				}
			/>
			<div className="flex flex-column gap-2">
				{!isError && isFetched && (
					// eslint-disable-next-line react/jsx-no-useless-fragment
					<>
						{numberOfPairs >= 1 && pairLimitReached ? (
							<MessageBox
								variant="purple"
								message={t('userPanel.pairing.congratulationsPairsDone')}
								highlight
							/>
						) : (
							<>
								{panelType === 'mentee' && (
									<MessageBox
										variant="purple"
										message={
											currentProgram.browseMembersByMentee
												? t('userPanel.pairing.infoMenteeLineCatalogMode')
												: [
														t('userPanel.pairing.infoMenteeLine1'),
														t('userPanel.pairing.infoMentorLine2'),
												  ]
										}
										highlight
									/>
								)}
								{panelType === 'mentor' && (
									<MessageBox
										variant="purple"
										message={[
											t('userPanel.pairing.infoMentorLine1'),
											t('userPanel.pairing.infoMenteeLine2'),
										]}
										highlight
									/>
								)}
							</>
						)}
					</>
				)}
				{isLoading && (
					<Loader
						text={t('userPanel.pairing.infoLoadingLine1')}
						subtext={t('userPanel.pairing.infoMentorLine2')}
					/>
				)}
				{!isLoading && isError && (
					<div className="flex flex-column justify-content-center align-items-center text-center">
						<p>{t('misc.genericError')}</p>
						<Button label={t('actions.tryAgain')} onClick={() => refetch()} />
					</div>
				)}
				{!isError && isFetched && (
					// eslint-disable-next-line react/jsx-no-useless-fragment
					<>
						{userPairs.length > 0 ? (
							<div>
								{userPairs.map((pairData) => {
									const isAccepted =
										pairData.acceptedByMentee !== null && pairData.acceptedByMentor !== null;

									return (
										<PairItem
											key={pairData.id}
											data={pairData}
											userAreas={userAreas}
											withActions
											withGreenBorder={isAccepted}
											onAction={handleAction}
										/>
									);
								})}
							</div>
						) : (
							<Box variant="white-bordered">
								<BoxSection>
									{!currentProgram.pairingStatus ? (
										<div>{t('userPanel.pairing.pairingOff')}</div>
									) : (
										<>
											{panelType === 'mentor' && (
												<>
													<div>{t('userPanel.pairing.noPairsMentor')}</div>
													<div>{t('userPanel.pairing.noPairsMentorRedirect')}</div>
												</>
											)}
											{panelType === 'mentee' && (
												<>
													{currentProgram.browseMembersByMentee && (
														<div>
															<Trans
																t={t}
																i18nKey="userPanel.pairing.noPairsMenteeCatalogMode"
																components={[
																	<Link to={`/${panelLinkPrefix}/browseMembers`} />,
																]}
															/>
														</div>
													)}
													{!currentProgram.browseMembersByMentee && (
														<div>{t('userPanel.pairing.noPairsMentee')}</div>
													)}
												</>
											)}
										</>
									)}
								</BoxSection>
							</Box>
						)}
					</>
				)}
			</div>
		</>
	);
};

export default Pairing;
